import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSolana } from '../context/WalletContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Global wallet mismatch guard - monitors connected wallet vs session wallet.
 * Uses aggressive polling to detect wallet changes from Phantom extension.
 */
export const WalletMismatchGuard = ({ children }) => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { publicKey, connected } = useSolana();
  const { t } = useTranslation();
  const isProcessing = useRef(false);
  const initialWallet = useRef(null);
  const disconnectTimer = useRef(null);

  const handleMismatch = useCallback(() => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    
    console.log('Wallet mismatch detected - logging out');
    
    toast.error(t('wallet.walletChanged'), {
      duration: 3000
    });
    
    // Clear all auth data and logout properly
    localStorage.removeItem('token');
    localStorage.setItem('wallet_auto_connect', 'false');
    logout();
    
    // Force full page reload to /home
    setTimeout(() => {
      window.location.replace('/home');
    }, 300);
  }, [t, logout]);

  const handleWalletDisconnected = useCallback(() => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    
    console.log('Wallet disconnected while session active - auto logout');
    
    toast.error('Wallet disconnected. Please reconnect to continue.', {
      duration: 3000
    });
    
    localStorage.removeItem('token');
    localStorage.setItem('wallet_auto_connect', 'false');
    logout();
    
    setTimeout(() => {
      window.location.replace('/home');
    }, 300);
  }, [logout]);

  // Set initial wallet when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.wallet_address && !initialWallet.current) {
      initialWallet.current = user.wallet_address;
      console.log('WalletMismatchGuard: Tracking session wallet:', user.wallet_address);
    }
    
    // Reset when logged out
    if (!isAuthenticated) {
      initialWallet.current = null;
      isProcessing.current = false;
      if (disconnectTimer.current) {
        clearTimeout(disconnectTimer.current);
        disconnectTimer.current = null;
      }
    }
  }, [isAuthenticated, user]);

  // Detect: user is authenticated with wallet_address but wallet is NOT connected
  // Give 8 seconds grace period for wallet adapter to auto-reconnect
  useEffect(() => {
    if (authLoading || isProcessing.current) return;
    if (!isAuthenticated || !user?.wallet_address) return;

    // Admin login without wallet — skip this check
    if (!user.wallet_address) return;

    if (!connected) {
      // Wallet not connected — start grace period timer
      if (!disconnectTimer.current) {
        console.log('WalletMismatchGuard: Wallet not connected, starting 8s grace period...');
        disconnectTimer.current = setTimeout(() => {
          // Re-check after grace period
          const provider = window.phantom?.solana || window.solana;
          const isWalletConnected = provider?.isConnected && provider?.publicKey;
          
          if (!isWalletConnected && isAuthenticated && !isProcessing.current) {
            console.log('WalletMismatchGuard: Grace period expired, wallet still disconnected');
            handleWalletDisconnected();
          }
          disconnectTimer.current = null;
        }, 8000);
      }
    } else {
      // Wallet reconnected — cancel grace period
      if (disconnectTimer.current) {
        console.log('WalletMismatchGuard: Wallet reconnected, canceling grace period');
        clearTimeout(disconnectTimer.current);
        disconnectTimer.current = null;
      }
    }

    return () => {
      if (disconnectTimer.current) {
        clearTimeout(disconnectTimer.current);
        disconnectTimer.current = null;
      }
    };
  }, [isAuthenticated, user, connected, authLoading, handleWalletDisconnected]);

  // Aggressive polling - check every 500ms
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user?.wallet_address) return;
    if (isProcessing.current) return;

    const sessionWallet = user.wallet_address;
    
    const checkWalletMismatch = () => {
      if (isProcessing.current) return;
      
      try {
        // Try multiple ways to get current wallet
        const provider = window.phantom?.solana || window.solana;
        
        if (provider && provider.isConnected && provider.publicKey) {
          const currentWallet = provider.publicKey.toString();
          
          // Log for debugging
          // console.log('Polling check:', { currentWallet, sessionWallet });
          
          if (currentWallet !== sessionWallet) {
            console.log('MISMATCH DETECTED via polling:', {
              current: currentWallet,
              session: sessionWallet
            });
            handleMismatch();
            return;
          }
        }
      } catch (err) {
        console.error('Error checking wallet:', err);
      }
    };

    // Check immediately on mount
    checkWalletMismatch();
    
    // Poll every 500ms for faster detection
    const pollInterval = setInterval(checkWalletMismatch, 500);

    // Also add visibility change listener - check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(checkWalletMismatch, 100);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check on window focus
    const handleFocus = () => {
      setTimeout(checkWalletMismatch, 100);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user, authLoading, handleMismatch]);

  // Secondary check using wallet adapter's publicKey
  useEffect(() => {
    if (authLoading || isProcessing.current) return;
    if (!isAuthenticated || !user?.wallet_address) return;
    
    if (connected && publicKey) {
      const adapterWallet = publicKey.toBase58();
      const sessionWallet = user.wallet_address;
      
      if (adapterWallet !== sessionWallet) {
        console.log('MISMATCH DETECTED via adapter:', {
          adapter: adapterWallet,
          session: sessionWallet
        });
        handleMismatch();
      }
    }
  }, [isAuthenticated, user, connected, publicKey, authLoading, handleMismatch]);

  return children;
};

export default WalletMismatchGuard;
